import type { ApiError } from '../http/errors.ts';
import { SEARCH_FIRST_PAGE } from './query.ts';

/**
 * "There is no page after this one", told apart from "this request failed".
 *
 * The API has no `continuation` and no total, so the only way to learn that a result set has ended is
 * to ask for the next page and be refused — and the refusal is not an empty list. It is Invidious
 * throwing:
 *
 * ```
 * GET /api/v1/search?q=lofi&type=video&page=35
 *   → 400 {"error":"Missing hash key: \"primaryContents\"","errorBacktrace":"… (KeyError)"}
 * ```
 *
 * That is the same leaked-exception shape §6 documents for comments, at a **400** instead of a 5xx —
 * which is why `isLeakedServerException` had to lose its status floor. Reached at page 35 for one
 * query and page 30 for another (and page 60 answered `200` again for the second), so it is YouTube's
 * continuation running out per query rather than a depth limit anyone can precompute.
 *
 * On **page 1** the same error means something else entirely: the query itself produced a shape
 * Invidious cannot read — which is what `type=show` does on 7 of 10 queries — and a reader must be
 * told the search failed, not that they have reached the end of it. So the page number is part of the
 * reading, and this function is the whole of that judgement.
 */
export function isSearchPagingExhausted(error: ApiError, page: number): boolean {
  return page > SEARCH_FIRST_PAGE && error.kind === 'upstream-broken';
}
